import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberAutogiroStatus } from '../models/memberAutogiroStatus';
import { MandateActionFilter } from '../components/mandateActionFilter';
import { useActionRequiredBadge } from '../hooks/useActionRequiredBadge';
import { MemberAutogiroStatusDataGrid } from '../components/memberAutogiroStatusDataGrid';

export const MemberAutogiroStatusOverviewPage = () => {
  const isMounted = useIsMounted();
  const location = useLocation();
  const { t } = useTranslation();
  const [actionRequiredCount] = useActionRequiredBadge();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [memberAutogiroStatusFilter, setMemberAutogiroStatusFilter] = useLocalStorage(LocalStorageKeys.MemberAutogiroStatusFilter, {
    sortCol: 'created',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberAutogiroStatus);

  const searchFilter: MemberAutogiroStatus = useMemo(() => {
    return {
      filter: memberAutogiroStatusFilter?.filter,
      pageSize: memberAutogiroStatusFilter?.pageSize,
      pageIndex: memberAutogiroStatusFilter?.pageIndex,
      sortCol: memberAutogiroStatusFilter?.sortCol,
      sortDir: memberAutogiroStatusFilter?.sortDir,
      onlyBlocked: memberAutogiroStatusFilter?.onlyBlocked,
    } as MemberAutogiroStatus;
  }, [
    memberAutogiroStatusFilter?.filter,
    memberAutogiroStatusFilter?.onlyBlocked,
    memberAutogiroStatusFilter?.pageIndex,
    memberAutogiroStatusFilter?.pageSize,
    memberAutogiroStatusFilter?.sortCol,
    memberAutogiroStatusFilter?.sortDir,
  ]);

  // Kommer man från Att hantera på faktureringsöversikten ska filtret redan vara på.
  useEffect(() => {
    const state = location.state as { onlyBlocked?: boolean } | null;

    if (state?.onlyBlocked) {
      setMemberAutogiroStatusFilter((prev) => ({ ...prev, onlyBlocked: true, pageIndex: 0 }));
      window.history.replaceState({}, '');
    }
    // Bara vid ankomst
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberAutogiroStatusFilter, tempSearchString]);

  return (
    <>
      <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t('invoice:autogiro.alertInfoText')}
      </Alert>
      <PageFilterSection
        leftContent={
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <SearchField
              initialValue={searchFilter.filter || ''}
              onSearch={(newSearchString) => {
                setSearchInitialized(true);
                setTempSearchString(newSearchString);
              }}
            />
            <MandateActionFilter
              value={!!searchFilter.onlyBlocked}
              count={actionRequiredCount}
              callback={(onlyBlocked) =>
                setMemberAutogiroStatusFilter((prev) => ({
                  ...prev,
                  onlyBlocked: onlyBlocked || undefined,
                  pageIndex: 0,
                }))
              }
            />
          </Box>
        }
      />
      <MemberAutogiroStatusDataGrid
        loadingCallback={(x) => ConsoleHelper.log(x)}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) =>
          setMemberAutogiroStatusFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
